import {
  Activity,
  Boxes,
  Briefcase,
  ChevronRight,
  CircleDashed,
  FileUp,
  Grid2x2,
  Inbox,
  LayoutGrid,
  LayoutList,
  Layers,
  ListChecks,
  LogOut,
  Radar,
  Replace,
  Tags,
  SlidersHorizontal,
  UserCog,
} from "lucide-react";
import { Suspense, lazy, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useJDs } from "@/api/jd-queries";
import { useConfig, useHealth, useStatusCounts, useSubmitJob } from "@/api/queries";
import { checkCan, useCan, useMember } from "@/auth/member";
import { goToSignOut } from "@/auth/session";
import { AccessDenied } from "@/components/AccessDenied";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/ui/logo-mark";
import { ErrorNote } from "@/components/ui/panel";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { cn } from "@/lib/utils";
import Access from "@/routes/Access";
import Buckets from "@/routes/Buckets";
import Clusters from "@/routes/Clusters";
import Concepts from "@/routes/Concepts";
import Config from "@/routes/Config";
import Ingest from "@/routes/Ingest";
import JobDescription from "@/routes/JobDescription";
import JobDescriptionUpload from "@/routes/JobDescriptionUpload";
import JobDescriptions from "@/routes/JobDescriptions";
import JDGaps from "@/routes/JDGaps";
import SkillAliases from "@/routes/SkillAliases";
import IngestRun from "@/routes/IngestRun";
import Probe from "@/routes/Probe";
import Picker from "@/routes/Picker";
import Questions from "@/routes/Questions";
import Review from "@/routes/Review";
import StackDetail from "@/routes/StackDetail";
import StackOverview from "@/routes/StackOverview";
import Stacks from "@/routes/Stacks";

/**
 * Metrics is the only screen that pulls in a charting library, and it more than
 * doubled the bundle when it was imported eagerly. Splitting it keeps that weight off
 * the four screens reviewers actually live in — §10's own argument for building them
 * in order of daily work carried.
 */
const Metrics = lazy(() => import("@/routes/Metrics"));

/**
 * ARCHITECTURE §10: build screens in order of daily work carried. Review is first
 * because it is the one people live in, and it was the weakest view in Streamlit —
 * `st.number_input("open question #")` to open a record was the ceiling showing.
 *
 * The second group is the engineering instruments. They are separated because nobody
 * outside the team opens them, not because they are less finished — the probe is how
 * a threshold change gets judged and Metrics is where a precision regression shows up.
 */
const NAV = [
  { to: "/review", label: "Review", icon: Inbox, permission: "page:review" },
  { to: "/questions", label: "Questions", icon: LayoutList, permission: "page:questions" },
  { to: "/stack-overview", label: "Stack Overview", icon: LayoutGrid, permission: "page:stack_overview" },
  { to: "/clusters", label: "Clusters", icon: Layers, permission: "page:clusters" },
  { to: "/ingest", label: "Ingest", icon: FileUp, permission: "page:ingest" },
];

/** DESIGN §20.11: the Resource Manager's work, kept apart from the bank's daily review. */
const JDS = [
  { to: "/jds", label: "All JDs", icon: Briefcase, permission: "page:jds" },
  // Phase 3: what the JDs in use ask for that the bank cannot ask yet, and the mappings people taught it.
  { to: "/jd-gaps", label: "Gap report", icon: CircleDashed, permission: "page:jd_gaps" },
  { to: "/jd-aliases", label: "Skill aliases", icon: Replace, permission: "page:jd_aliases" },
];

const TOOLS = [
  { to: "/picker", label: "Picker", icon: ListChecks, permission: "page:picker" },
  { to: "/probe", label: "Probe", icon: Radar, permission: "page:probe" },
  { to: "/metrics", label: "Metrics", icon: Activity, permission: "page:metrics" },
];

/** Admins only: who holds which role (ARCHITECTURE §11) and the settings the back office tunes live. */
const ADMIN = [
  { to: "/config", label: "Config", icon: SlidersHorizontal, permission: "page:config" },
  { to: "/access", label: "Access", icon: UserCog, permission: "page:access" },
];

const TAXONOMY = [
  { to: "/stacks", label: "Stacks", icon: Boxes, permission: "page:taxonomy" },
  { to: "/concepts", label: "Concepts", icon: Tags, permission: "page:taxonomy" },
  { to: "/buckets", label: "Buckets", icon: Grid2x2, permission: "page:taxonomy" },
];

/**
 * The active item gets a wash and a 2px stripe at the rail edge rather than a grey
 * fill. A fill in a neutral palette reads as "hovered" as easily as "here", and the
 * stripe survives being glanced at from across a desk.
 */
function NavItem({ to, label, icon: Icon, badge }: (typeof NAV)[number] & { badge?: React.ReactNode }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "relative flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
            isActive ? "bg-bank-accent-wash text-bank-accent-ink font-medium" : "hover:bg-bank-surface-2",
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                className="bg-bank-accent absolute top-1.5 bottom-1.5 -left-2 w-0.5 rounded-r"
                aria-hidden="true"
              />
            )}
            <Icon
              className={cn("size-4 shrink-0", isActive ? "text-bank-accent" : "text-bank-muted")}
              aria-hidden="true"
            />
            {label}
            {badge}
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * §8.6's daily cap is the thing to check before starting a run, and "14/20" reads
 * slower than a bar that is most of the way across. Amber past 70% because that is
 * the point where a pipeline run may not finish on what is left.
 */
function QuotaMeter({ model, used, cap }: { model: string; used: number; cap: number }) {
  const share = cap > 0 ? Math.min(used / cap, 1) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-bank-muted truncate font-mono text-[11px]" title={model}>
          {model}
        </span>
        <span className="font-mono text-[11px] tabular-nums">
          {used}/{cap}
        </span>
      </div>
      <div className="bg-bank-surface-3 h-[3px] overflow-hidden rounded-full">
        <div
          className={cn("h-full rounded-full", share > 0.7 ? "bg-bank-attention" : "bg-bank-accent")}
          style={{ width: `${share * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Rows sitting between `extracted` and `dedup_checked` — carried this far by a
 * requeue, a re-enrich, or a fresh ingest, and going nowhere until something submits
 * a `pipeline` job. Ambient because a requeue on Review or Questions leaves this the
 * only place that still says so once the reviewer has moved on.
 */
function PipelineBacklog() {
  const { data } = useStatusCounts();
  const submit = useSubmitJob();
  const canRun = useCan("run_pipeline");
  const backlog =
    (data?.counts.extracted ?? 0) +
    (data?.counts.enriched ?? 0) +
    (data?.counts.embedded ?? 0) +
    (data?.counts.dedup_checked ?? 0);
  if (!backlog) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-bank-muted font-mono text-[11px] tabular-nums"
          title="extracted + enriched + embedded + dedup_checked — short of pending_review"
        >
          {backlog} waiting on pipeline
        </span>
        {canRun && (
          <Button size="sm" disabled={submit.isPending} onClick={() => submit.mutate("pipeline")}>
            {submit.isPending ? "Queuing…" : "Run"}
          </Button>
        )}
      </div>
      <ErrorNote error={submit.error} />
    </div>
  );
}

/**
 * What Streamlit's sidebar carried: which models are answering, under which dedup
 * config_version, and how much of today's quota is gone.
 *
 * Quota lines appear only for models that have actually been called today — §8.6's
 * cap is the thing to check before starting a run, and a row of "0/1000" every
 * morning trains people to stop reading it.
 */
function ConfigNote() {
  const { data } = useConfig();
  // Collapsed by default so the sidebar fits without a scrollbar; the detail is a click away.
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const spent = data.quota.filter((q) => q.used > 0);
  return (
    <div className="space-y-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-bank-muted hover:text-bank-ink flex w-full items-center gap-1 font-mono text-[10px] tracking-[0.1em] uppercase"
      >
        <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} aria-hidden="true" />
        AI API details
      </button>
      {open && spent.length > 0 && (
        <div className="space-y-2.5">
          {spent.map((q) => (
            <QuotaMeter key={q.model} model={q.model} used={q.used} cap={q.daily_cap} />
          ))}
        </div>
      )}
      {open && (
        <dl className="text-bank-muted grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 font-mono text-[11px]">
          <dt className="opacity-75">llm</dt>
          <dd
            className="text-bank-ink truncate"
            title={`${data.backend} · ${data.llm_model} · adjudication ${data.adjudication_model}`}
          >
            {data.llm_model}
          </dd>
          <dt className="opacity-75">emb</dt>
          <dd className="text-bank-ink truncate" title={data.embedding_model}>
            {data.embedding_dimensions}d
          </dd>
          <dt className="opacity-75">dedup</dt>
          <dd className="text-bank-ink truncate" title={`dedup config_version ${data.dedup_config_version}`}>
            {data.dedup_config_version.slice(0, 8)}
          </dd>
        </dl>
      )}
    </div>
  );
}

function HealthDot() {
  const { data, isError } = useHealth();
  const ok = !isError && data?.status === "ok";
  const label = isError
    ? "API unreachable"
    : ok
      ? `database reachable · ${data?.latency_ms ?? "?"} ms`
      : (data?.database ?? "checking");
  return (
    <span className="text-bank-muted flex items-center gap-1.5 text-[11px]" title={label}>
      <span className={cn("size-1.5 rounded-full", ok ? "bg-bank-good" : "bg-bank-bad")} aria-hidden="true" />
      {ok ? `healthy · ${data?.latency_ms ?? "?"} ms` : "degraded"}
    </span>
  );
}

/**
 * Who is signed in and as what. The role is shown because it explains every missing button: a
 * Viewer sees the bank but none of the controls that change it (core/access.py).
 */
function MemberNote() {
  const me = useMember();
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium" title={me.email || undefined}>
          {me.full_name || me.member_no}
        </p>
        <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">
          {me.role} · {me.member_no}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <ThemeToggle />
        <Button
          size="sm"
          variant="ghost"
          title="Sign out of the Question Bank"
          onClick={() => void goToSignOut()}
        >
          <LogOut className="size-4" aria-hidden="true" />
          <span className="sr-only">Sign out</span>
        </Button>
      </div>
    </div>
  );
}

function PendingCount() {
  const { data } = useStatusCounts();
  const pending = (data?.counts.pending_review ?? 0) + (data?.counts.stale ?? 0);
  if (!pending) return null;
  return (
    <span className="bg-bank-attention-wash text-bank-attention-ink ml-auto rounded px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums">
      {pending}
    </span>
  );
}

/** JDs waiting on a Resource Manager — the same "somebody's job" count Review carries. */
function JDReviewCount() {
  const { data } = useJDs();
  const waiting = (data?.items ?? []).filter((jd) => jd.status === "in_review").length;
  if (!waiting) return null;
  return (
    <span className="bg-bank-attention-wash text-bank-attention-ink ml-auto rounded px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums">
      {waiting}
    </span>
  );
}

function RouteGuard({
  can,
  requiredPermission,
  children,
}: {
  can: boolean;
  requiredPermission?: string;
  children: React.ReactNode;
}) {
  if (!can) {
    return <AccessDenied requiredPermission={requiredPermission} />;
  }
  return <>{children}</>;
}

/**
 * Collapsible so the rail stays short enough to show every group header without
 * scrolling. Items are filtered dynamically based on current user's role permissions.
 */
function NavGroup({
  title,
  items,
  defaultOpen = false,
  badges,
}: {
  title: string;
  items: (typeof NAV)[number][];
  defaultOpen?: boolean;
  badges?: Record<string, React.ReactNode>;
}) {
  const { pathname } = useLocation();
  const me = useMember();
  const visibleItems = items.filter((i) => ("permission" in i && i.permission ? checkCan(me, i.permission) : true));
  const holdsRoute = visibleItems.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  const [open, setOpen] = useState(defaultOpen || holdsRoute);

  if (visibleItems.length === 0) return null;

  return (
    <div className="p-2 pt-0 first:pt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-bank-muted hover:text-bank-ink flex w-full items-center gap-1 px-2 pt-1.5 pb-2 font-mono text-[10px] tracking-[0.1em] uppercase"
      >
        <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} aria-hidden="true" />
        {title}
      </button>
      {open && (
        <ul className="space-y-0.5">
          {visibleItems.map((item) => (
            <NavItem key={item.to} {...item} badge={badges?.[item.to]} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function App() {
  const me = useMember();
  const canAdmin = checkCan(me, "page:config") || checkCan(me, "page:access") || checkCan(me, "manage_access");

  const defaultRoute = useMemo(() => {
    const allItems = [...NAV, ...JDS, ...TAXONOMY, ...TOOLS, ...ADMIN];
    const match = allItems.find((i) => ("permission" in i && i.permission ? checkCan(me, i.permission) : true));
    return match ? match.to : "/review";
  }, [me]);

  return (
    <div className="flex h-full overflow-hidden">
      <nav className="border-bank-border bg-bank-surface flex h-full min-h-0 w-60 shrink-0 flex-col border-r">
        <div className="border-bank-border flex shrink-0 items-center gap-3 border-b px-4 py-3">
          <span className="bg-bank-brand text-bank-on-brand grid size-10 shrink-0 place-items-center rounded-xl shadow-xs transition-colors">
            <LogoMark className="size-[26px]" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-base leading-tight font-semibold tracking-tight">Question Bank</p>
            <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">Management</p>
            <HealthDot />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <NavGroup title="Daily" items={NAV} defaultOpen badges={{ "/review": <PendingCount /> }} />
          <NavGroup title="Job descriptions" items={JDS} defaultOpen badges={{ "/jds": <JDReviewCount /> }} />
          <NavGroup title="Taxonomy" items={TAXONOMY} />
          {canAdmin && <NavGroup title="Admin" items={ADMIN} />}
          <NavGroup title="Instruments" items={TOOLS} />
        </div>

        <div className="border-bank-border shrink-0 space-y-3 border-t px-4 py-3">
          <PipelineBacklog />
          <ConfigNote />
        </div>

        <div className="border-bank-border shrink-0 border-t px-4 py-2.5">
          <MemberNote />
        </div>
      </nav>

      <main className="min-w-0 flex-1 overflow-auto">
        <Suspense fallback={<p className="text-bank-muted p-4 text-sm">Loading…</p>}>
          <Routes>
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />
            <Route
              path="/review"
              element={
                <RouteGuard can={checkCan(me, "page:review")} requiredPermission="Review Page Access">
                  <Review />
                </RouteGuard>
              }
            />
            <Route
              path="/questions"
              element={
                <RouteGuard can={checkCan(me, "page:questions")} requiredPermission="Questions Page Access">
                  <Questions />
                </RouteGuard>
              }
            />
            <Route
              path="/stack-overview"
              element={
                <RouteGuard can={checkCan(me, "page:stack_overview")} requiredPermission="Stack Overview Access">
                  <StackOverview />
                </RouteGuard>
              }
            />
            <Route
              path="/stack-overview/:stackPath"
              element={
                <RouteGuard can={checkCan(me, "page:stack_overview")} requiredPermission="Stack Overview Access">
                  <StackDetail />
                </RouteGuard>
              }
            />
            <Route
              path="/clusters"
              element={
                <RouteGuard can={checkCan(me, "page:clusters")} requiredPermission="Clusters Page Access">
                  <Clusters />
                </RouteGuard>
              }
            />
            <Route
              path="/ingest"
              element={
                <RouteGuard can={checkCan(me, "page:ingest")} requiredPermission="Ingest Page Access">
                  <Ingest />
                </RouteGuard>
              }
            />
            <Route
              path="/ingest/runs/:sourceDoc"
              element={
                <RouteGuard can={checkCan(me, "page:ingest")} requiredPermission="Ingest Page Access">
                  <IngestRun />
                </RouteGuard>
              }
            />
            <Route
              path="/jds"
              element={
                <RouteGuard can={checkCan(me, "page:jds")} requiredPermission="Job Descriptions Page Access">
                  <JobDescriptions />
                </RouteGuard>
              }
            />
            <Route
              path="/jds/new"
              element={
                <RouteGuard
                  can={checkCan(me, "page:jds") && checkCan(me, "create:jds")}
                  requiredPermission="Upload Job Description"
                >
                  <JobDescriptionUpload />
                </RouteGuard>
              }
            />
            <Route
              path="/jds/:id"
              element={
                <RouteGuard
                  can={checkCan(me, "page:jds") && checkCan(me, "read:jds")}
                  requiredPermission="View Job Description"
                >
                  <JobDescription />
                </RouteGuard>
              }
            />
            <Route
              path="/jd-gaps"
              element={
                <RouteGuard can={checkCan(me, "page:jd_gaps")} requiredPermission="Gap Report Access">
                  <JDGaps />
                </RouteGuard>
              }
            />
            <Route
              path="/jd-aliases"
              element={
                <RouteGuard can={checkCan(me, "page:jd_aliases")} requiredPermission="Skill Aliases Access">
                  <SkillAliases />
                </RouteGuard>
              }
            />
            <Route
              path="/stacks"
              element={
                <RouteGuard can={checkCan(me, "page:taxonomy")} requiredPermission="Taxonomy Page Access">
                  <Stacks />
                </RouteGuard>
              }
            />
            <Route
              path="/concepts"
              element={
                <RouteGuard can={checkCan(me, "page:taxonomy")} requiredPermission="Taxonomy Page Access">
                  <Concepts />
                </RouteGuard>
              }
            />
            <Route
              path="/buckets"
              element={
                <RouteGuard can={checkCan(me, "page:taxonomy")} requiredPermission="Taxonomy Page Access">
                  <Buckets />
                </RouteGuard>
              }
            />
            <Route
              path="/picker"
              element={
                <RouteGuard can={checkCan(me, "page:picker")} requiredPermission="Picker Page Access">
                  <Picker />
                </RouteGuard>
              }
            />
            <Route
              path="/probe"
              element={
                <RouteGuard can={checkCan(me, "page:probe")} requiredPermission="Similarity Probe Access">
                  <Probe />
                </RouteGuard>
              }
            />
            <Route
              path="/metrics"
              element={
                <RouteGuard can={checkCan(me, "page:metrics")} requiredPermission="Metrics Page Access">
                  <Metrics />
                </RouteGuard>
              }
            />
            <Route
              path="/config"
              element={
                <RouteGuard can={checkCan(me, "page:config")} requiredPermission="System Config Access">
                  <Config />
                </RouteGuard>
              }
            />
            <Route
              path="/access"
              element={
                <RouteGuard
                  can={checkCan(me, "page:access") || checkCan(me, "manage_access")}
                  requiredPermission="Access & Roles Management"
                >
                  <Access />
                </RouteGuard>
              }
            />
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
